import { useCallback, useMemo } from 'react';
import { PromiseState } from 'react-refetch';
import useCountries from 'hooks/useCountries';

import { ICountryFlagProps } from 'types/shared';
import {
  IRadioSpins,
  ICountryRadioSpinsDataProps,
  ICountryITem,
  ICountryUpdated,
  ICountryMetadata,
} from 'components/data-containers/KPIs/types';

/**
 * This hook normalizes country radio spins data to a usable format.
 * It also adds country metadata to the spins data.
 *
 * @param {object} countryRadioSpinsData A react-refetch PromiseState that resolves to country radio spins data.
 * @returns {array} normalized radio spins data
 */

const useCountryRadioSpinsData = (
  countryRadioSpinsData: PromiseState<ICountryRadioSpinsDataProps[]>
): IRadioSpins[] | undefined => {
  const countriesMetadata = useCountries();

  const normalizeCountrySpins = useCallback(
    (el) => {
      const { entity, countries } = el;

      const normalizeCountry = (country: ICountryITem): ICountryUpdated => ({
        id: country.countryId,
        name: country.countryName,
        data: country.data,
      });

      const addMetadata = (country: ICountryUpdated): ICountryMetadata => {
        const byCountryId =
          (countryId: string) =>
          (el: ICountryFlagProps): boolean =>
            el.id === countryId;

        return {
          ...country,
          metadata: countriesMetadata.find(byCountryId(country.id)),
        };
      };

      return {
        entity,
        data: countries.map(normalizeCountry).map(addMetadata),
      };
    },
    [countriesMetadata]
  );

  const countryRadioSpins = useMemo(() => {
    if (countryRadioSpinsData.fulfilled) {
      const countryRadioSpins = countryRadioSpinsData.value;
      return countryRadioSpins.map(normalizeCountrySpins);
    }
  }, [
    countryRadioSpinsData.fulfilled,
    countryRadioSpinsData.value,
    normalizeCountrySpins,
  ]);

  return countryRadioSpins;
};

export default useCountryRadioSpinsData;
